import type { en } from "./en";

/** Danish UI strings. Typed against the English dictionary so no key can be missing. */
export const da: Record<keyof typeof en, string> = {
  // --- Fælles ---------------------------------------------------------------
  "common.app_name": "Dolphin Booking",
  "common.language": "Sprog",
  "common.loading": "Indlæser",
  "common.save": "Gem",
  "common.saved": "Gemt.",
  "common.cancel": "Annuller",
  "common.back": "Tilbage",
  "common.close": "Luk",
  "common.confirm": "Bekræft",
  "common.delete": "Slet",
  "common.edit": "Rediger",
  "common.add": "Tilføj",
  "common.remove": "Fjern",
  "common.optional": "(valgfrit)",
  "common.day": "Dag",
  "common.today": "I dag",
  "common.previous": "Forrige",
  "common.next": "Næste",
  "common.start": "Start",
  "common.end": "Slut",
  "common.minutes": "{n} min",
  "common.error_generic": "Noget gik galt. Prøv igen.",
  "common.error_load": "Siden kunne ikke indlæses. Genindlæs for at prøve igen.",
  "common.free": "Gratis",
  "common.yes": "Ja",
  "common.no": "Nej",

  // --- Navigation ------------------------------------------------------------
  "nav.main": "Hovedmenu",
  "nav.overview": "Overblik",
  "nav.bookings": "Bookinger",
  "nav.practitioners": "Behandlere",
  "nav.services": "Ydelser",
  "nav.settings": "Klinikindstillinger",
  "nav.profile": "Profil",
  "nav.sign_out": "Log ud",
  "nav.sign_in": "Log ind",
  "nav.start_free": "Kom i gang gratis",
  "nav.open_dashboard": "Åbn oversigten",
  "nav.clinic": "Klinik",

  // --- Forside ---------------------------------------------------------------
  "landing.tagline": "Online booking til klinikker",
  "landing.headline": "Lad patienterne booke online. Brug tiden på behandlinger.",
  "landing.lede":
    "Dolphin Booking er bookingsystemet til selvstændige klinikker: fysioterapeuter, kiropraktorer, massører og mange flere. Patienterne booker ledige tider døgnet rundt, og bekræftelser og påmindelser sendes helt af sig selv.",
  "landing.cta_setup": "Opret din klinik",
  "landing.cta_account": "Jeg har allerede en konto",
  "landing.trust_eu": "Hostet i EU",
  "landing.trust_gdpr": "Bygget til GDPR",
  "landing.trust_lang": "Dansk og engelsk",
  "landing.hero_alt": "Dolphin Bookings delfin, der springer op af en bølge",
  "landing.features_title": "Alt hvad en travl klinik har brug for, og intet den ikke har",
  "landing.feature1_title": "Online tidsbestilling 24/7",
  "landing.feature1_text":
    "Patienterne ser de tider, der reelt er ledige, og booker på under et minut uden at oprette en konto.",
  "landing.feature2_title": "Ingen dobbeltbookinger",
  "landing.feature2_text": "Ledighed tjekkes pr. behandler og håndhæves af databasen selv.",
  "landing.feature3_title": "Automatiske påmindelser",
  "landing.feature3_text":
    "Bekræftelser, besked ved ændringer og påmindelser 24 timer før på e-mail, og på SMS hvis du ønsker det.",
  "landing.feature4_title": "Din kalender, synkroniseret",
  "landing.feature4_text": "Hver behandler får et privat kalenderfeed til Google, Apple eller Outlook-kalenderen.",
  "landing.feature5_title": "Bygget til teams",
  "landing.feature5_text": "Inviter behandlere og administratorer, og sæt arbejdstider og ydelser pr. person.",
  "landing.feature6_title": "Indsigt",
  "landing.feature6_text": "Se bookinger, aflysninger, udeblivelser og jeres travleste timer med ét blik.",
  "landing.steps_title": "I gang på tre trin",
  "landing.step1_title": "Opret din klinik",
  "landing.step1_text": "Tilføj ydelser, priser og behandlere med deres arbejdstider.",
  "landing.step2_title": "Del din bookingside",
  "landing.step2_text": "Link til den fra jeres hjemmeside, Google-profil eller sociale medier.",
  "landing.step3_title": "Patienterne booker selv",
  "landing.step3_text":
    "Bookingerne lander i kalenderen, og patienterne kan flytte eller aflyse inden for jeres regler.",
  "landing.audience_title": "Lavet til selvstændige klinikker",
  "landing.audience_list":
    "Fysioterapeuter · Kiropraktorer · Massører · Osteopater · Psykologer · Zoneterapeuter · Skønhedsklinikker",
  "landing.privacy_title": "Databeskyttelse indbygget fra start",
  "landing.privacy_text":
    "Patienternes data er adskilt pr. klinik i databasen, hostet i EU og bruges kun til jeres bookinger. Ingen reklamer, ingen sporing og ingen cookiebannere. I får en databehandleraftale og værktøjer til at udlevere eller slette en patients data.",
  "landing.privacy_link": "Læs privatlivspolitikken",
  "landing.faq_title": "Ofte stillede spørgsmål",
  "landing.faq1_q": "Skal mine patienter have en konto for at booke?",
  "landing.faq1_a":
    "Nej. Patienterne booker med navn og e-mail og får et link, hvor de kan se, flytte eller aflyse bookingen.",
  "landing.faq2_q": "Overholder Dolphin Booking GDPR?",
  "landing.faq2_a":
    "Ja. Data hostes i EU, hver kliniks data er adskilt, gamle data anonymiseres automatisk, og alle klinikker får en databehandleraftale.",
  "landing.faq3_q": "Kan patienterne få påmindelser på SMS?",
  "landing.faq3_a": "Ja. Påmindelser på e-mail er altid med, og I kan slå SMS-påmindelser til 24 timer før hver tid.",
  "landing.faq4_q": "Findes bookingsiden på dansk og engelsk?",
  "landing.faq4_a": "Ja. Patienterne vælger selv sprog, og e-mails følger det sprog, de bookede på.",
  "landing.faq5_q": "Kan jeg sætte bookingsiden på min egen hjemmeside?",
  "landing.faq5_a":
    "Ja. Hver klinik får sin egen bookingadresse, som I kan linke til fra hjemmesiden, Google-profilen eller sociale medier.",
  "landing.cta_title": "Klar til at fylde kalenderen?",
  "landing.cta_text": "Opret din klinik på få minutter.",

  // --- Login m.m. ------------------------------------------------------------
  "auth.accept_terms_before": "På klinikkens vegne accepterer jeg",
  "auth.accept_terms_and": "og",
  "auth.accept_terms_after": ".",
  "auth.signup_title": "Opret din klinik",
  "auth.already_registered": "Allerede oprettet?",
  "auth.new_here": "Ny her?",
  "auth.clinic_name": "Klinikkens navn",
  "auth.booking_address": "Adresse på bookingsiden",
  "auth.booking_address_hint": "Små bogstaver, tal og bindestreger",
  "auth.booking_address_preview": "Patienter booker på {url}",
  "auth.your_name": "Dit navn",
  "auth.work_email": "Arbejds-e-mail",
  "auth.email": "E-mail",
  "auth.password": "Adgangskode",
  "auth.password_hint": "Mindst {n} tegn. En kort sætning fungerer godt.",
  "auth.create_clinic": "Opret klinik",
  "auth.sign_in": "Log ind",
  "auth.forgot_password": "Glemt adgangskode?",
  "auth.login_failed": "Forkert e-mail eller adgangskode",
  "auth.reset_title": "Nulstil din adgangskode",
  "auth.reset_subtitle": "Vi sender dig et link til at vælge en ny.",
  "auth.reset_sent": "Hvis der findes en konto med den e-mail, er et link på vej. Det udløber om 30 minutter.",
  "auth.send_reset_link": "Send link",
  "auth.back_to_sign_in": "Tilbage til login",
  "auth.new_password_title": "Vælg en ny adgangskode",
  "auth.link_incomplete": "Linket er ufuldstændigt. Bed om en ny e-mail.",
  "auth.password_changed_signed_out": "Din adgangskode er ændret. Alle andre sessioner er logget ud.",
  "auth.new_password": "Ny adgangskode",
  "auth.new_password_hint": "Mindst {n} tegn.",
  "auth.repeat_new_password": "Gentag ny adgangskode",
  "auth.passwords_mismatch": "Adgangskoderne er ikke ens",
  "auth.change_password": "Skift adgangskode",
  "auth.not_found_title": "Siden blev ikke fundet",
  "auth.not_found_text": "Siden, du ledte efter, findes ikke.",
  "auth.go_home": "Gå til forsiden",

  // --- Oversigt ----------------------------------------------------------------
  "app.account_load_failed": "Din konto kunne ikke indlæses. Genindlæs siden for at prøve igen.",
  "app.not_linked": "Din konto er endnu ikke knyttet til en klinik.",
  "app.welcome": "Velkommen, {name}",
  "app.role_owner": "Ejer",
  "app.role_admin": "Administrator",
  "app.role_practitioner": "Behandler",
  "app.active_services": "Aktive ydelser",
  "app.practitioners": "Behandlere",
  "app.bookings_today": "Bookinger i dag",
  "app.get_started": "Kom i gang",
  "app.get_started_services": "Tilføj de ydelser, patienterne kan booke.",
  "app.add_first_service": "Tilføj din første ydelse →",
  "app.get_started_practitioners": "Tilføj en behandler med arbejdstider, så patienterne kan booke.",
  "app.add_first_practitioner": "Tilføj en behandler →",
  "app.booking_page": "Din bookingside",
  "app.booking_page_text": "Del dette link med patienterne. Det virker, så snart en behandler har arbejdstider.",
  "app.open_booking_page": "Åbn bookingsiden",

  // --- Ydelser -----------------------------------------------------------------
  "services.title": "Ydelser",
  "services.subtitle": "Hvad patienterne kan booke, hvor lang tid det tager, og hvad det koster.",
  "services.show_inactive": "Vis inaktive",
  "services.load_failed": "Ydelserne kunne ikke indlæses.",
  "services.empty": "Ingen ydelser endnu.",
  "services.deactivate": "Deaktiver",
  "services.activate": "Aktiver",
  "services.add_title": "Tilføj en ydelse",
  "services.name": "Navn",
  "services.name_placeholder": "fx Første konsultation",
  "services.duration": "Varighed (minutter)",
  "services.price": "Pris (DKK)",
  "services.description": "Beskrivelse (valgfrit)",
  "services.add_button": "Tilføj ydelse",

  // --- Klinikindstillinger ------------------------------------------------------
  "settings.title": "Klinikindstillinger",
  "settings.load_failed": "Indstillingerne kunne ikke indlæses.",
  "settings.saved": "Indstillingerne er gemt.",
  "settings.clinic_name": "Klinikkens navn",
  "settings.timezone": "Tidszone",
  "settings.timezone_hint": "IANA-navn, fx Europe/Copenhagen. Arbejdstider og ledige tider bruger denne tidszone.",
  "settings.booking_page": "Bookingside",
  "settings.default_language": "Bookingsidens sprog",
  "settings.default_language_hint": "Vises til patienterne, indtil de selv vælger sprog.",
  "settings.customer_accounts": "Lad patienter oprette konti",
  "settings.customer_accounts_text":
    "Patienter kan altid booke uden konto. Med konti slået til kan de også logge ind og se og ændre deres bookinger.",
  "settings.public_profile": "Offentlig profil",
  "settings.booking_rules": "Bookingregler",
  "settings.slot_interval": "Minutter mellem starttider",
  "settings.min_notice": "Mindste varsel for onlinebooking (minutter)",
  "settings.max_advance": "Hvor langt frem patienter kan booke (dage)",
  "settings.cancellation_notice": "Patienter kan flytte eller aflyse online indtil (minutter før)",
  "settings.save": "Gem indstillinger",
  "lang.en": "Engelsk",
  "lang.da": "Dansk",

  // --- Profil ---------------------------------------------------------------------
  "profile.title": "Profil",
  "profile.details": "Dine oplysninger",
  "profile.name": "Navn",
  "profile.email": "E-mail",
  "profile.change_password": "Skift adgangskode",
  "profile.change_password_text": "Du forbliver logget ind her; andre enheder logges ud.",
  "profile.current_password": "Nuværende adgangskode",
  "profile.new_password": "Ny adgangskode",
  "profile.password_changed": "Adgangskoden er ændret.",

  // --- Behandlere -------------------------------------------------------------------
  "practitioners.title": "Behandlere",
  "practitioners.subtitle": "Hvem patienterne kan booke, hvilke ydelser de tilbyder, og hvornår de arbejder.",
  "practitioners.show_inactive": "Vis inaktive",
  "practitioners.empty": "Ingen behandlere endnu.",
  "practitioners.load_failed": "Behandlerne kunne ikke indlæses.",
  "practitioners.add_title": "Tilføj en behandler",
  "practitioners.name": "Navn",
  "practitioners.job_title": "Titel (valgfrit)",
  "practitioners.job_title_placeholder": "fx Fysioterapeut",
  "practitioners.services": "Tilbudte ydelser",
  "practitioners.no_services_hint": "Tilføj ydelser først, og knyt dem så her.",
  "practitioners.add_button": "Tilføj behandler",
  "practitioners.edit_title": "Rediger behandler",
  "practitioners.active": "Aktiv (kan bookes online)",
  "practitioners.inactive": "inaktiv",
  "practitioners.no_hours": "Ingen arbejdstider endnu",
  "practitioners.hours_summary": "{days} dage om ugen",
  "practitioners.working_hours": "Arbejdstider",
  "practitioners.working_hours_hint":
    "Tider er i klinikkens tidszone ({tz}). Tilføj flere blokke på en dag for at holde pause.",
  "practitioners.add_block": "Tilføj tider",
  "practitioners.save_hours": "Gem arbejdstider",
  "practitioners.hours_saved": "Arbejdstiderne er gemt.",
  "practitioners.time_off": "Fravær",
  "practitioners.time_off_hint": "Ferie, kurser eller en fraværsdag. Der tilbydes ingen tider under fravær.",
  "practitioners.time_off_empty": "Intet fravær de næste 90 dage.",
  "practitioners.time_off_reason": "Årsag (valgfrit)",
  "practitioners.add_time_off": "Tilføj fravær",
  "practitioners.time_off_added": "Fravær tilføjet.",
  "practitioners.saved": "Behandleren er gemt.",
  "weekday.1": "Mandag",
  "weekday.2": "Tirsdag",
  "weekday.3": "Onsdag",
  "weekday.4": "Torsdag",
  "weekday.5": "Fredag",
  "weekday.6": "Lørdag",
  "weekday.7": "Søndag",

  // --- Bookinger (personale) ------------------------------------------------------------
  "bookings.title": "Bookinger",
  "bookings.subtitle": "Aftaler pr. dag. Flyt, aflys eller registrer fremmøde.",
  "bookings.practitioner": "Behandler",
  "bookings.all_practitioners": "Alle behandlere",
  "bookings.status": "Status",
  "bookings.all_statuses": "Alle statusser",
  "bookings.status_confirmed": "Bekræftet",
  "bookings.status_cancelled": "Aflyst",
  "bookings.status_completed": "Gennemført",
  "bookings.status_no_show": "Udeblevet",
  "bookings.empty": "Ingen bookinger denne dag.",
  "bookings.load_failed": "Bookingerne kunne ikke indlæses.",
  "bookings.new_booking": "Ny booking",
  "bookings.new_hint":
    "Booking ved fremmøde eller telefon. Onlinereglerne (varsel og arbejdstider) gælder ikke; overlap forhindres stadig.",
  "bookings.service": "Ydelse",
  "bookings.date": "Dato",
  "bookings.time": "Tidspunkt",
  "bookings.customer_name": "Patientens navn",
  "bookings.customer_email": "Patientens e-mail",
  "bookings.customer_phone": "Telefon (valgfrit)",
  "bookings.notes": "Noter (valgfrit)",
  "bookings.language": "Patientens sprog (e-mails)",
  "bookings.send_confirmation": "Send en bekræftelse til patienten",
  "bookings.create_button": "Opret booking",
  "bookings.created": "Bookingen er oprettet.",
  "bookings.cancel_booking": "Aflys booking",
  "bookings.cancel_confirm": "Aflys denne booking? Patienten får en e-mail.",
  "bookings.cancel_reason": "Årsag (valgfrit)",
  "bookings.cancelled": "Bookingen er aflyst.",
  "bookings.mark_completed": "Gennemført",
  "bookings.mark_no_show": "Udeblevet",
  "bookings.customer_data": "Kundedata",
  "bookings.customer_data_text":
    "Download alt, der er gemt om {name} (ved en anmodning om indsigt eller dataportabilitet), eller slet kundens personoplysninger. Sletning anonymiserer kunden, fjerner noter fra alle kundens bookinger og aflyser fremtidige bookinger uden at give kunden besked. Det kan ikke fortrydes.",
  "bookings.customer_export": "Download data (JSON)",
  "bookings.customer_erase": "Slet kunde",
  "bookings.customer_erase_confirm": "Slet alle personoplysninger om {name}? Det kan ikke fortrydes.",
  "bookings.customer_erased": "Kundens personoplysninger er slettet.",
  "bookings.move": "Flyt",
  "bookings.move_title": "Flyt booking",
  "bookings.move_hint": "Vælg en ny dato og et nyt tidspunkt. Patienten får en e-mail.",
  "bookings.move_button": "Flyt booking",
  "bookings.moved": "Bookingen er flyttet.",
  "bookings.updated": "Bookingen er opdateret.",
  "bookings.source_online": "Booket online",
  "bookings.source_staff": "Booket af personale",
  "bookings.moved_from": "Flyttet",
  "bookings.with": "hos {name}",

  // --- Offentlig bookingside -----------------------------------------------------------
  "book.clinic_not_found": "Vi kunne ikke finde denne klinik.",
  "book.step_service": "Vælg en ydelse",
  "book.step_practitioner": "Vælg en behandler",
  "book.any_practitioner": "Første ledige behandler",
  "book.step_time": "Vælg et tidspunkt",
  "book.step_details": "Dine oplysninger",
  "book.no_services": "Klinikken har ingen ydelser, der kan bookes lige nu.",
  "book.no_slots_day": "Ingen ledige tider denne dag.",
  "book.no_slots_week": "Ingen ledige tider denne uge.",
  "book.times_in": "Tider vises i {tz}.",
  "book.full_name": "Fulde navn",
  "book.email": "E-mail",
  "book.phone": "Telefon (valgfrit)",
  "book.notes": "Er der noget, vi skal vide? (valgfrit)",
  "book.notes_hint":
    "Skriv venligst ikke følsomme helbredsoplysninger her. Dem kan du fortælle din behandler, når du kommer.",
  "book.privacy_notice":
    "{clinic} bruger dine oplysninger til at håndtere bookingen og sende dig en bekræftelse og påmindelser. Dolphin Booking behandler dem på klinikkens vegne.",
  "book.privacy_link": "Sådan behandles dine data",
  "book.confirm_button": "Bekræft booking",
  "book.summary": "Din booking",
  "book.change": "Ændr",
  "book.confirmed_title": "Bookingen er bekræftet",
  "book.confirmed_text":
    "Vi har sendt en bekræftelse til {email} med et link til at se, flytte eller aflyse bookingen.",
  "book.view_booking": "Se din booking",
  "book.book_another": "Book en ny tid",
  "book.powered_by": "Online booking fra Dolphin Booking",
  "book.manage_title": "Din booking",
  "book.manage_not_found": "Vi kunne ikke finde denne booking. Tjek linket i din e-mail.",
  "book.status_cancelled": "Denne booking er aflyst.",
  "book.status_completed": "Denne aftale har fundet sted.",
  "book.status_no_show": "Denne aftale er registreret som udeblivelse.",
  "book.cancellable_until": "Du kan flytte eller aflyse online indtil {when}.",
  "book.too_late": "Ændringer er ikke længere mulige online. Kontakt venligst klinikken.",
  "book.cancel_button": "Aflys booking",
  "book.cancel_confirm": "Er du sikker på, at du vil aflyse denne booking?",
  "book.cancel_reason": "Årsag (valgfrit)",
  "book.cancelled_done": "Din booking er aflyst.",
  "book.move_button": "Flyt booking",
  "book.move_title": "Vælg et nyt tidspunkt",
  "book.moved_done": "Din booking er flyttet.",
  "book.keep_time": "Behold det nuværende tidspunkt",
  "book.service": "Ydelse",
  "book.practitioner": "Behandler",
  "book.when": "Hvornår",
  "book.name": "Navn",

  // --- Fejlkoder fra API'et ---------------------------------------------------------------
  "errors.SLUG_TAKEN": "Den adresse til bookingsiden er optaget.",
  "errors.EMAIL_TAKEN": "Der findes allerede en konto med denne e-mail. Log ind i stedet.",
  "errors.INVALID_OR_EXPIRED_TOKEN": "Linket er ugyldigt eller udløbet.",
  "errors.PASSWORD_UNCHANGED": "Vælg en anden adgangskode end den nuværende.",
  "errors.PASSWORD_CHANGE_REQUIRED": "Skift startadgangskoden først.",
  "errors.INVALID_CURRENT_PASSWORD": "Den nuværende adgangskode er forkert.",
  "errors.SLOT_UNAVAILABLE": "Tidspunktet er ikke ledigt. Vælg venligst et andet.",
  "errors.SLOT_TAKEN": "Tidspunktet blev lige booket af en anden. Vælg venligst et andet.",
  "errors.BOOKING_NOT_CANCELLABLE": "Bookingen kan ikke længere aflyses.",
  "errors.BOOKING_NOT_CHANGEABLE": "Bookingen kan ikke længere ændres.",
  "errors.TOO_LATE_TO_CANCEL": "Bookingen kan ikke længere aflyses online. Kontakt venligst klinikken.",
  "errors.TOO_LATE_TO_CHANGE": "Bookingen kan ikke længere ændres online. Kontakt venligst klinikken.",
  "errors.INVALID_STATUS_TRANSITION": "Bookingens status kan ikke ændres på den måde.",
  "errors.PRACTITIONER_NOT_FOUND": "Behandleren findes ikke eller tilbyder ikke denne ydelse.",
  "errors.SERVICE_NOT_FOUND": "Ydelsen findes ikke.",
  "errors.BOOKING_NOT_FOUND": "Bookingen findes ikke.",
  "errors.CLINIC_NOT_FOUND": "Klinikken findes ikke.",
  "errors.UNAUTHENTICATED": "Log ind for at fortsætte.",
  "errors.FORBIDDEN": "Du har ikke tilladelse til dette.",
  "errors.RATE_LIMITED": "For mange forsøg. Vent et øjeblik, og prøv igen.",
  "errors.NETWORK_ERROR": "Serveren kunne ikke nås. Tjek din forbindelse, og prøv igen.",
  "errors.INTERNAL_ERROR": "Noget gik galt hos os. Prøv igen.",
  "errors.VALIDATION_ERROR": "Tjek venligst de markerede felter.",
  "errors.BAD_ORIGIN": "Anmodningen blev afvist af sikkerhedshensyn. Genindlæs siden, og prøv igen.",

  // --- Team (CB-23) ----------------------------------------------------------------------
  "team.title": "Team",
  "team.subtitle": "Hvem kan logge ind i denne klinik. Inviter kolleger via e-mail.",
  "team.members": "Medlemmer",
  "team.remove": "Fjern",
  "team.remove_confirm": "Fjern {name} fra klinikken?",
  "team.invitations": "Åbne invitationer",
  "team.invitations_empty": "Ingen åbne invitationer.",
  "team.invite_title": "Inviter en kollega",
  "team.invite_email": "E-mail",
  "team.invite_role": "Rolle",
  "team.role_admin": "Administrator",
  "team.role_practitioner": "Behandler",
  "team.role_owner": "Ejer",
  "team.role_admin_hint": "Kan administrere ydelser, behandlere, bookinger og indstillinger.",
  "team.role_practitioner_hint": "Kan se ydelser, behandlere og bookinger.",
  "team.invite_button": "Send invitation",
  "team.invited": "Invitationen er sendt.",
  "team.revoke": "Tilbagekald",
  "team.expires": "Udløber {date}",
  "invite.title": "Bliv en del af {clinic}",
  "invite.text": "Du er inviteret til {clinic} som {role}.",
  "invite.email": "E-mail",
  "invite.your_name": "Dit navn",
  "invite.password": "Adgangskode",
  "invite.password_existing_hint": "Denne e-mail har allerede en konto. Indtast dens adgangskode for at blive medlem.",
  "invite.password_new_hint": "Mindst {n} tegn. En kort sætning fungerer godt.",
  "invite.accept": "Accepter og log ind",
  "invite.not_open": "Invitationen er ikke længere gyldig. Bed klinikken om at sende en ny.",
  "invite.not_found": "Vi kunne ikke finde invitationen. Tjek linket i din e-mail.",
  "clinics.new_title": "Opret endnu en klinik",
  "clinics.new_text": "Du bliver dens ejer. Dine nuværende klinikker påvirkes ikke.",
  "clinics.new_button": "Opret klinik",
  "clinics.new_link": "+ Ny klinik",

  // --- Offentlig oversigt ----------------------------------------------------------------
  "directory.title": "Find en klinik",
  "directory.subtitle": "Klinikker, der tager imod onlinebookinger.",
  "directory.search": "Søg på navn eller adresse",
  "directory.empty": "Ingen klinikker matcher. Prøv en anden søgning.",
  "directory.book": "Book",
  "nav.directory": "Find en klinik",
  "settings.directory": "Vis klinikken i den offentlige oversigt",
  "settings.directory_text":
    "Patienter kan finde jer på navn eller adresse. Adresse og beskrivelse vises på bookingsiden.",
  "settings.address": "Adresse",
  "settings.phone": "Telefon",
  "settings.description": "Beskrivelse",
  "settings.sms": "Send også sms-påmindelser",
  "settings.sms_text":
    "24 timer før aftalen, til patienter der har oplyst telefonnummer. Kræver en sms-udbyder på serveren.",

  // --- Indsigt (CB-25) ----------------------------------------------------------------------
  "nav.team": "Team",
  "nav.insights": "Indsigt",
  "insights.title": "Indsigt",
  "insights.subtitle": "Bookinger, omsætning og hvor fyldt kalenderen er.",
  "insights.range_week": "Denne uge",
  "insights.range_month": "Denne måned",
  "insights.range_30": "Seneste 30 dage",
  "insights.range_90": "Seneste 90 dage",
  "insights.bookings": "Bookinger",
  "insights.completed": "Gennemført",
  "insights.cancelled": "Aflyst",
  "insights.no_show": "Udeblevet",
  "insights.revenue": "Omsætning (gennemført)",
  "insights.expected": "Forventet (inkl. kommende)",
  "insights.online_share": "Booket online",
  "insights.by_day": "Bookinger pr. dag",
  "insights.by_practitioner": "Behandlernes udnyttelse",
  "insights.by_practitioner_hint": "Bookede minutter som andel af planlagte arbejdstimer i perioden.",
  "insights.booked": "Booket",
  "insights.scheduled": "Planlagt",
  "insights.utilization": "Udnyttelse",
  "insights.no_hours": "Ingen timer",
  "insights.by_service": "Ydelser",
  "insights.empty": "Ingen bookinger i perioden endnu.",

  // --- Kalenderabonnement (CB-27) ------------------------------------------------------------
  "calendar.title": "Kalenderabonnement",
  "calendar.text":
    "Et privat link, som Google Kalender, Outlook eller Apple Kalender kan abonnere på. Det viser behandlerens aftaler og opdateres automatisk.",
  "calendar.issue": "Opret link",
  "calendar.reissue": "Opret et nyt link",
  "calendar.revoke": "Fjern link",
  "calendar.issued": "Kopiér linket nu; det vises kun én gang. Oprettes et nyt link, virker det gamle ikke længere.",
  "calendar.active": "Der findes et abonnementslink.",
  "errors.ALREADY_MEMBER": "Personen er allerede medlem af klinikken.",
  "errors.LAST_OWNER": "En klinik skal have mindst én ejer.",
  "errors.INVITATION_NOT_OPEN": "Invitationen er ikke længere gyldig.",
  "errors.INVITATION_NOT_FOUND": "Invitationen findes ikke.",
  "errors.MEMBER_NOT_FOUND": "Personen er ikke medlem af klinikken.",

  // --- Abonnementstilstand (banner, indstillinger) ------------------------------
  "sub.state_trialing": "Prøveperiode",
  "sub.state_active": "Aktivt",
  "sub.state_past_due": "Forfaldent",
  "sub.state_expired": "Udløbet",
  "sub.state_cancelled": "Opsagt",
  "sub.state_suspended": "Suspenderet",
  "sub.state_none": "Intet abonnement",
  "sub.banner_trialing":
    "Gratis prøveperiode: {n} dage tilbage. Kontakt platformen for at fortsætte efter prøveperioden.",
  "sub.banner_trial_last_day": "Din gratis prøveperiode udløber i dag. Kontakt platformen for at fortsætte.",
  "sub.banner_past_due":
    "Betalingen for dit abonnement er forfalden. Onlinebooking lukker om få dage, medmindre den bliver betalt.",
  "sub.banner_expired": "Dit abonnement er udløbet. Din bookingside er lukket for nye bookinger, indtil det fornys.",
  "sub.banner_cancelled": "Dit abonnement er opsagt. Din bookingside er lukket for nye bookinger.",
  "sub.banner_suspended": "Klinikken er suspenderet af platformen. {reason}",
  "settings.subscription": "Abonnement",
  "settings.sub_text": "Abonnementer administreres af platformen. Kontakt os for at skifte plan.",
  "settings.sub_plan": "Plan",
  "settings.sub_status": "Status",
  "settings.sub_price": "Pris",
  "settings.sub_trial_ends": "Prøveperioden udløber",
  "settings.sub_paid_until": "Betalt til",
  "settings.sub_practitioners": "Behandlere inkluderet",
  "settings.sub_unlimited": "Ubegrænset",
  "sub.per_month": "pr. måned",
  "sub.per_year": "pr. år",
  "book.closed_title": "Onlinebooking er sat på pause",
  "book.closed_text": "Klinikken tager ikke imod onlinebookinger lige nu. Kontakt venligst klinikken direkte.",

  // --- Platformadministration ---------------------------------------------------
  "admin.title": "Platformadmin",
  "admin.login.title": "Log ind i administrationskonsollen",
  "admin.login.subtitle": "Kun for platformadministratorer.",
  "admin.nav.password": "Skift adgangskode",
  "admin.password.title": "Skift adgangskode",
  "admin.password.first_time":
    "Det er første gang, du logger ind. Erstat startadgangskoden fra serverkonfigurationen med din egen, før du fortsætter.",
  "admin.password.text": "Du forbliver logget ind her; dine andre sessioner logges ud.",
  "admin.nav.overview": "Overblik",
  "admin.nav.clinics": "Klinikker",
  "admin.nav.users": "Brugere",
  "admin.nav.plans": "Planer",
  "admin.nav.audit": "Hændelseslog",
  "admin.nav.settings": "Indstillinger",
  "admin.ip_blocked":
    "Din adresse {ip} er ikke på adminlisten. Tilføj den til ADMIN_IP_ALLOWLIST på serveren, eller bed en anden admin om at tilføje den under Indstillinger.",
  "admin.overview.title": "Overblik",
  "admin.overview.clinics": "Klinikker",
  "admin.overview.active": "aktive",
  "admin.overview.suspended": "suspenderede",
  "admin.overview.new_30": "Nye på 30 dage",
  "admin.overview.users": "Brugere",
  "admin.overview.bookings_30": "Bookinger på 30 dage",
  "admin.overview.bookings_total": "Bookinger i alt",
  "admin.overview.emails_30": "E-mails sendt på 30 dage",
  "admin.overview.sms_30": "SMS sendt på 30 dage",
  "admin.overview.by_state": "Klinikker efter abonnementstilstand",
  "admin.overview.recent": "Seneste tilmeldinger",
  "admin.overview.unavailable": "–",
  "admin.clinics.title": "Klinikker",
  "admin.clinics.subtitle": "Alle klinikker på platformen med ejer og abonnement.",
  "admin.clinics.search": "Søg på navn, adresse eller ejerens e-mail",
  "admin.clinics.filter_state": "Tilstand",
  "admin.clinics.all": "Alle",
  "admin.clinics.empty": "Ingen klinikker matcher.",
  "admin.clinics.owner": "Ejer",
  "admin.clinics.plan": "Plan",
  "admin.clinics.created": "Oprettet",
  "admin.clinics.members": "Medlemmer",
  "admin.clinics.practitioners": "Behandlere",
  "admin.clinics.bookings_30": "Bookinger (30 d.)",
  "admin.clinics.open": "Åbn",
  "admin.clinics.total": "{n} klinikker",
  "admin.clinic.details": "Detaljer",
  "admin.clinic.timezone": "Tidszone",
  "admin.clinic.address": "Adresse",
  "admin.clinic.phone": "Telefon",
  "admin.clinic.bookings_total": "Bookinger i alt",
  "admin.clinic.services": "Aktive ydelser",
  "admin.clinic.listed": "I klinikoversigten",
  "admin.clinic.sms": "SMS-påmindelser",
  "admin.clinic.booking_page": "Bookingside",
  "admin.clinic.members": "Medlemmer",
  "admin.clinic.suspend": "Suspendér klinik",
  "admin.clinic.suspend_reason": "Begrundelse (vises til klinikkens personale)",
  "admin.clinic.suspend_confirm": "Suspendér {name}? Bookingsiden lukker med det samme.",
  "admin.clinic.unsuspend": "Ophæv suspendering",
  "admin.clinic.suspended_since": "Suspenderet siden {date}",
  "admin.clinic.audit": "Adminhandlinger på denne klinik",
  "admin.sub.title": "Abonnement",
  "admin.sub.plan": "Plan",
  "admin.sub.currency": "Valuta",
  "admin.sub.interval": "Betalingsperiode",
  "admin.sub.interval_month": "Månedligt",
  "admin.sub.interval_year": "Årligt",
  "admin.sub.status": "Status",
  "admin.sub.status_trialing": "Prøveperiode",
  "admin.sub.status_active": "Aktivt",
  "admin.sub.status_past_due": "Forfaldent",
  "admin.sub.status_cancelled": "Opsagt",
  "admin.sub.trial_ends": "Prøveperioden udløber",
  "admin.sub.period_end": "Betalt til",
  "admin.sub.period_end_hint": "Lad feltet stå tomt for løbende abonnement (manuel fakturering).",
  "admin.sub.grace": "Efter betalt-til-datoen er der {n} dages frist, før onlinebooking lukker.",
  "admin.sub.external_ref": "Ekstern reference",
  "admin.sub.notes": "Noter",
  "admin.sub.save": "Gem abonnement",
  "admin.sub.saved": "Abonnementet er gemt.",
  "admin.sub.price": "Pris",
  "admin.sub.no_price": "Ingen pris for denne kombination",
  "admin.users.title": "Brugere",
  "admin.users.subtitle": "Alle med en personalekonto på tværs af klinikker.",
  "admin.users.search": "Søg på e-mail eller navn",
  "admin.users.empty": "Ingen brugere matcher.",
  "admin.users.no_clinics": "Ingen klinikker",
  "admin.users.joined": "Oprettet {date}",
  "admin.plans.title": "Planer",
  "admin.plans.subtitle": "Hvad klinikkerne betaler. Nye klinikker starter på standardplanen med dens prøveperiode.",
  "admin.plans.new": "Ny plan",
  "admin.plans.edit": "Redigér",
  "admin.plans.code": "Kode",
  "admin.plans.code_hint": "Kort id, fx standard. Små bogstaver, tal og bindestreger.",
  "admin.plans.name": "Navn",
  "admin.plans.description": "Beskrivelse",
  "admin.plans.trial_days": "Prøvedage",
  "admin.plans.max_practitioners": "Maks. behandlere",
  "admin.plans.max_practitioners_hint": "Lad feltet stå tomt for ubegrænset.",
  "admin.plans.is_default": "Standardplan for nye tilmeldinger",
  "admin.plans.active": "Kan vælges til nye abonnementer",
  "admin.plans.sort_order": "Sortering",
  "admin.plans.prices": "Priser",
  "admin.plans.prices_hint": "Beløb i hele enheder (fx 299). Lad feltet stå tomt for ikke at tilbyde kombinationen.",
  "admin.plans.monthly": "Månedligt",
  "admin.plans.yearly": "Årligt",
  "admin.plans.clinics": "{n} klinikker",
  "admin.plans.save": "Gem plan",
  "admin.plans.saved": "Planen er gemt.",
  "admin.plans.default": "Standard",
  "admin.plans.inactive": "Inaktiv",
  "admin.plans.unlimited": "Ubegrænset antal behandlere",
  "admin.plans.max_n": "Op til {n} behandlere",
  "admin.plans.trial_n": "{n} dages prøveperiode",
  "admin.plans.cancel_edit": "Annullér",
  "admin.audit.title": "Hændelseslog",
  "admin.audit.subtitle": "Alle ændringer og log-ind i administrationskonsollen.",
  "admin.audit.empty": "Intet endnu.",
  "admin.audit.when": "Hvornår",
  "admin.audit.who": "Hvem",
  "admin.audit.action": "Handling",
  "admin.audit.target": "Mål",
  "admin.audit.details": "Detaljer",
  "admin.settings.title": "Indstillinger",
  "admin.settings.ip_title": "Admin-IP-liste",
  "admin.settings.ip_text":
    "Adminområdet kan kun åbnes fra disse adresser. Adresser fra serverkonfigurationen (ADMIN_IP_ALLOWLIST) gælder altid; dem du tilføjer her gemmes i databasen og kan ændres uden en ny udrulning.",
  "admin.settings.ip_configured": "Fra serverkonfiguration",
  "admin.settings.ip_extra": "Yderligere adresser (én pr. linje)",
  "admin.settings.ip_hint": "IPv4/IPv6-adresser eller CIDR-områder, fx 203.0.113.4 eller 10.0.0.0/8.",
  "admin.settings.your_ip": "Din nuværende adresse: {ip}",
  "admin.settings.add_mine": "Tilføj min adresse",
  "admin.settings.save": "Gem liste",
  "admin.settings.saved": "Listen er gemt.",
  "common.search": "Søg",
  "common.load_more": "Vis flere",
  "common.none": "Ingen",
  "errors.CLINIC_NOT_ACCEPTING_BOOKINGS": "Klinikken tager ikke imod onlinebookinger i øjeblikket.",
  "errors.ADMIN_IP_NOT_ALLOWED": "Din adresse er ikke på adminlisten.",
  "errors.PLAN_LIMIT_PRACTITIONERS": "Din plan tillader ikke flere behandlere. Kontakt platformen for at opgradere.",
  "errors.PLAN_CODE_TAKEN": "Der findes allerede en plan med denne kode.",
  "errors.DEFAULT_PLAN_REQUIRED": "Gør først en anden plan til standard.",
  "errors.DEFAULT_PLAN_INACTIVE": "Standardplanen skal kunne vælges.",
  "errors.PRICE_NOT_DEFINED": "Planen har ingen pris for den valuta og betalingsperiode.",
  "errors.TRIAL_END_REQUIRED": "En prøveperiode skal have en slutdato.",
  "errors.WOULD_LOCK_YOU_OUT": "Det ville fjerne din egen adresse fra listen.",
  "errors.CANNOT_CHANGE_SELF": "Du kan ikke ændre din egen adminadgang.",
  "errors.ALREADY_SUSPENDED": "Klinikken er allerede suspenderet.",
  "errors.NOT_SUSPENDED": "Klinikken er ikke suspenderet.",
  "errors.PLAN_NOT_FOUND": "Planen findes ikke.",
  "errors.USER_NOT_FOUND": "Brugeren findes ikke.",
  "errors.DUPLICATE_PRICE": "Samme valuta og periode er angivet to gange.",

  // --- Sidefod og juridiske sider ---------------------------------------------------
  "footer.product": "Produkt",
  "footer.legal": "Juridisk",
  "footer.privacy": "Privatlivspolitik",
  "footer.terms": "Vilkår og betingelser",
  "footer.dpa": "Databehandleraftale",
  "footer.cookies": "Cookiepolitik",
  "footer.cvr": "CVR {cvr}",
  "footer.no_tracking": "Ingen sporing og ingen reklamecookies.",
  "legal.other_docs": "Andre dokumenter",
};
